import type { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { createLoadingShimmerStyles } from '@bloomays-lib/ui.shared';

export type PrimeValueKpiVariant = 'default' | 'green' | 'negative' | 'highlight';

export type PrimeValueKpiProps = {
  title: string;
  value: string;
  loading?: boolean;
  variant?: PrimeValueKpiVariant;
  subtitle?: string;
  titleAdornment?: ReactNode;
  onClick?: () => void;
  ariaLabel?: string;
};

const PrimeValueKpi = ({
  title,
  value,
  loading = false,
  variant = 'default',
  subtitle,
  titleAdornment,
  onClick,
  ariaLabel,
}: PrimeValueKpiProps) => (
  <KpiContainer
    $loading={loading}
    $variant={variant}
    $clickable={Boolean(onClick)}
    onClick={onClick}
    aria-label={ariaLabel}
    onKeyDown={
      onClick
        ? (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onClick();
            }
          }
        : undefined
    }
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
  >
    <TitleRow $variant={variant}>
      <KpiTitle $variant={variant}>{title}</KpiTitle>
      {titleAdornment}
    </TitleRow>
    <KpiValue $variant={variant}>{value}</KpiValue>
    {subtitle ? <KpiSubtitle $variant={variant}>{subtitle}</KpiSubtitle> : null}
  </KpiContainer>
);

export default PrimeValueKpi;

const KpiContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== '$loading' && prop !== '$variant' && prop !== '$clickable',
})<{ $loading: boolean; $variant: PrimeValueKpiVariant; $clickable: boolean }>(
  ({ theme, $loading, $variant, $clickable }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: theme.spacing(1.25),
    flex: $variant === 'highlight' ? '1.1 1 0' : '1 1 0',
    minWidth: 0,
    padding: $variant === 'highlight' ? '16px 22px' : '18px 22px',
    ...($variant === 'highlight'
      ? {
          color: theme.palette.neutral.white,
          background: `radial-gradient(130% 160% at 100% 0%, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 52%), linear-gradient(118deg, ${theme.palette.red[80]} -10%, ${theme.palette.red[60]} 62%, ${theme.palette.red[40]} 130%)`,
        }
      : {}),
    ...createLoadingShimmerStyles({ active: $loading, contentOpacity: 0.6, speedMs: 1200, lightOpacity: 0.6 }),
    ...($clickable
      ? {
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.neutral[15],
          },
        }
      : {}),
    [theme.breakpoints.down('md')]: {
      flex: '1 1 100%',
    },
  }),
);

const TitleRow = styled('div', {
  shouldForwardProp: (prop) => prop !== '$variant',
})<{ $variant: PrimeValueKpiVariant }>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  minWidth: 0,
}));

const KpiTitle = styled('span', {
  shouldForwardProp: (prop) => prop !== '$variant',
})<{ $variant: PrimeValueKpiVariant }>(({ theme, $variant }) => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '10.5px',
  fontWeight: 600,
  letterSpacing: $variant === 'highlight' ? '0.06em' : '0.05em',
  textTransform: 'uppercase',
  color: $variant === 'highlight' ? 'rgba(255, 255, 255, 0.95)' : theme.palette.neutral[50],
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
}));

const KpiValue = styled('span', {
  shouldForwardProp: (prop) => prop !== '$variant',
})<{ $variant: PrimeValueKpiVariant }>(({ theme, $variant }) => ({
  fontFamily: '"Poppins Bold", sans-serif',
  fontSize: $variant === 'highlight' ? '21px' : '20px',
  fontWeight: 700,
  lineHeight: $variant === 'highlight' ? 1.2 : 1.1,
  whiteSpace: 'nowrap',
  color:
    $variant === 'highlight'
      ? '#FFFFFF'
      : $variant === 'green'
      ? theme.palette.green[100]
      : $variant === 'negative'
      ? theme.palette.red[100]
      : theme.palette.neutral[90],
}));

const KpiSubtitle = styled('span', {
  shouldForwardProp: (prop) => prop !== '$variant',
})<{ $variant: PrimeValueKpiVariant }>(({ theme, $variant }) => ({
  fontFamily: '"Poppins Medium", sans-serif',
  fontSize: $variant === 'highlight' ? '14px' : '13px',
  fontWeight: 500,
  lineHeight: 1.3,
  color: $variant === 'highlight' ? 'rgba(255, 255, 255, 0.92)' : theme.palette.neutral[60],
}));
